import { PORTAL_ESTIMATE_NOTICE, type PortalViewOutput, type WorkItemOutput } from '@garage/shared';
import { LinesList } from '../../garage/components/LinesList.js';
import { frDecimal } from '../../garage/lib/decimal.js';
import { formatDateTime } from '../../garage/lib/format.js';
import { Money, hasAmount } from '../../garage/ui/money.js';
import { cx } from '../../ui/cx.js';
import { ComplementDecision } from './ComplementDecision.js';
import { PortalPhotos } from './PortalPhotos.js';
import { Block, CARD } from './portal-ui.js';

/** Travaux réalisés, chacun avec son montant TTC (s'il est renvoyé), puis pièces et références. */
function WorkDone({ items, totalTtc, report }: { items: WorkItemOutput[]; totalTtc: string | null | undefined; report: boolean }) {
  if (items.length === 0) {
    return (
      <Block title="Travaux réalisés">
        <p className="text-ink-3">
          {report ? 'Aucun travail n’a été enregistré pour cette intervention.' : 'Les travaux réalisés s’afficheront ici au fil de l’intervention.'}
        </p>
      </Block>
    );
  }
  const parts = items.filter((w) => w.kind === 'part');
  return (
    <>
      <Block title="Travaux réalisés">
        <div className={CARD}>
          <LinesList lines={items.map((w) => ({ ...w, key: w.id }))} totalTtc={totalTtc} totalLabel="Total des travaux TTC" showUnitPrice={false} />
        </div>
      </Block>
      {parts.length > 0 ? (
        <Block title="Pièces et références">
          <ul className="divide-y divide-line-soft rounded-2xl border border-line bg-surface px-4 sm:px-5">
            {parts.map((p) => (
              <li key={p.id} className="flex items-baseline justify-between gap-3 py-3">
                <span className="min-w-0 text-ink">
                  {p.label}
                  {p.reference ? <span className="block font-mono text-sm text-ink-3">Réf. {p.reference}</span> : null}
                </span>
                <span className="shrink-0 text-sm text-ink-3 tabular-nums">× {frDecimal(p.quantity)}</span>
              </li>
            ))}
          </ul>
        </Block>
      ) : null}
    </>
  );
}

/**
 * Travaux prévus rédigés par le garage et montant estimé. Le montant n'apparaît que s'il a été renvoyé par
 * l'API, et **toujours** avec sa mention d'estimation indicative (jamais une facture : Pennylane fait foi).
 */
function PlannedWork({ work, report }: { work: PortalViewOutput['work']; report: boolean }) {
  const planned = work.plannedWork?.trim() || null;
  const estimate = work.estimate;
  const amount = hasAmount(estimate?.totalTtc) ? estimate?.totalTtc : null;
  if (!planned && !amount) {
    if (report) return null;
    return (
      <Block title="Travaux prévus" id="travaux-prevus">
        <p className="text-ink-3">Le garage précisera ici les travaux prévus.</p>
      </Block>
    );
  }
  return (
    <Block title="Travaux prévus" id="travaux-prevus">
      <div className={cx(CARD, 'space-y-4')}>
        {planned ? <p className="whitespace-pre-line text-ink">{planned}</p> : null}
        {amount ? (
          <div className={cx(planned && 'border-t border-line-soft pt-4')} data-testid="estimate">
            <p className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
              <span className="text-ink-2">Montant estimé</span>
              <Money value={amount} suffix="TTC" className="text-lg font-semibold text-ink" />
            </p>
            <p className="mt-2 rounded-xl bg-sunken p-3 text-sm text-ink-2">{estimate?.notice || PORTAL_ESTIMATE_NOTICE}</p>
          </div>
        ) : null}
      </div>
    </Block>
  );
}

/**
 * Onglet Travaux : travaux complémentaires à décider (en tête : c'est l'action attendue), demande du client,
 * travaux prévus et montant estimé, photos, travaux complémentaires déjà décidés, travaux réalisés.
 * Décisions inchangées : version exacte, idempotence (`ComplementDecision`). Compte rendu : lecture seule.
 */
export function WorkTab({ view }: { view: PortalViewOutput }) {
  const { intervention: it } = view;
  const report = view.mode === 'report';
  // `decidable` renvoyé par l'API fait foi (une version reportée reste décidable).
  const toDecide = report ? [] : view.complements.filter((c) => c.decidable);
  const others = report ? view.complements : view.complements.filter((c) => !c.decidable);
  // Travaux réalisés de l'intervention du lien seulement (CONTRACT §18), total TTC exact du serveur.
  const done = view.work.done;

  return (
    <>
      {toDecide.length > 0 ? (
        <Block title="Travaux complémentaires à valider" id="decision">
          <p className="mb-3 text-base text-ink-2">Montants toutes taxes comprises (TTC). Votre choix est transmis tout de suite au garage.</p>
          <div className="space-y-4">
            {toDecide.map((c) => (
              <ComplementDecision key={c.id} complement={c} />
            ))}
          </div>
        </Block>
      ) : null}

      <Block title="Votre demande">
        <p className={`${CARD} whitespace-pre-line text-ink`}>{it.customerRequest}</p>
        {report ? null : <p className="mt-2 text-sm text-ink-3">Véhicule déposé le {formatDateTime(it.receivedAt, it.timezone)}</p>}
      </Block>

      <PlannedWork work={view.work} report={report} />

      {view.media.length > 0 ? (
        <Block title="Photos" id="photos">
          <PortalPhotos media={view.media} />
        </Block>
      ) : null}

      {others.length > 0 ? (
        <Block title="Travaux complémentaires">
          <div className="space-y-4">
            {others.map((c) => (
              <ComplementDecision key={c.id} complement={c} readOnly={report} />
            ))}
          </div>
        </Block>
      ) : null}

      <WorkDone items={done.items} totalTtc={done.totalTtc} report={report} />
    </>
  );
}
